import {
  getPullRequestSearchQueries,
  PullRequestPickerFragment,
  PullRequestPickerSearchPullRequestsGraphQLQuery,
} from '@github-ui/item-picker/PullRequestPicker'
import type {
  PullRequestPickerPullRequest$data,
  PullRequestPickerPullRequest$key,
} from '@github-ui/item-picker/PullRequestPickerPullRequest.graphql'
import type {PullRequestPickerSearchQuery} from '@github-ui/item-picker/PullRequestPickerSearchQuery.graphql'
import type {RepositoryPickerRepository$data} from '@github-ui/item-picker/RepositoryPickerRepository.graphql'
import {ActionList, Button, SelectPanel, Box, Text} from '@primer/react'
import {CopilotIcon} from '@primer/octicons-react'
import {Suspense, useCallback, useEffect, useMemo, useState} from 'react'
import {graphql, readInlineData, useFragment, useQueryLoader} from 'react-relay'

import {HOTKEYS} from '../../../constants/hotkeys'
import {LABELS} from '../../../constants/labels'
import {TEST_IDS} from '../../../constants/test-ids'
import {VALUES} from '../../../constants/values'
import type {
  DevelopmentSectionFragment$data,
  DevelopmentSectionFragment$key,
} from './__generated__/DevelopmentSectionFragment.graphql'
import {LinkedPullRequests} from './LinkedPullRequests'
import {ReadonlySectionHeader} from '@github-ui/issue-metadata/ReadonlySectionHeader'
import {Section} from '@github-ui/issue-metadata/Section'
import {SectionHeader} from '@github-ui/issue-metadata/SectionHeader'
import {DevelopmentSectionEditorNew} from './DevelopmentSectionEditorNew'
import {BranchPickerSearchBranches, BranchPickerRefFragment} from '@github-ui/item-picker/BranchPicker'
import type {BranchPickerRef$data, BranchPickerRef$key} from '@github-ui/item-picker/BranchPickerRef.graphql'
import type {BranchPickerSearchBranchesQuery} from '@github-ui/item-picker/BranchPickerSearchBranchesQuery.graphql'
import {LinkedBranches} from './LinkedBranches'
import {NoBranchesOrLinkedPullRequests} from './NoBranchesOrLinkedPullRequests'
import {BranchNextStepLocal} from './BranchNextStepLocal'
import type {BranchNextStep} from './CreateBranchDialog'

import {useKeyPress} from '@github-ui/use-key-press'
import {BranchNextStepDesktop} from './BranchNextStepDesktop'
import {noop} from '@github-ui/noop'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'

const developmentSectionFragment = graphql`
  fragment DevelopmentSectionFragment on Issue {
    repository {
      name
      owner {
        login
      }
    }
    id
    title
    number
    linkedBranches(first: 25) {
      edges {
        node {
          id
          ref {
            ...BranchPickerRef
          }
        }
      }
    }
    closedByPullRequestsReferences(first: 10, includeClosedPrs: true) {
      edges {
        node {
          ...PullRequestPickerPullRequest
        }
      }
    }
    ...LinkedPullRequests
    viewerCanUpdateNext
  }
`

export const DevelopmentSectionReposGraphqlQuery = graphql`
  query DevelopmentSectionReposQuery($owner: String!, $repo: String!) {
    repository(owner: $owner, name: $repo) {
      # eslint-disable-next-line relay/must-colocate-fragment-spreads This will be fixed on the item picker is refactored
      ...RepositoryPickerRepository
    }
    viewer {
      # eslint-disable-next-line relay/must-colocate-fragment-spreads This will be fixed on the item picker is refactored
      ...RepositoryPickerTopRepositories
    }
  }
`

export const DevelopmentSectionBranchesGraphqlQuery = graphql`
  query DevelopmentSectionBranchesQuery($owner: String!, $repo: String!) {
    repository(owner: $owner, name: $repo) {
      ...BranchPickerRepositoryBranchRefs
    }
  }
`

export const usePullRequestsQueryRef = (repo?: RepositoryPickerRepository$data) => {
  const [pullRequestsQueryRef, loadPullRequestsQuery, disposePullRequests] =
    useQueryLoader<PullRequestPickerSearchQuery>(PullRequestPickerSearchPullRequestsGraphQLQuery)

  useEffect(() => {
    if (repo) {
      const searchQueries = getPullRequestSearchQueries(`${repo.owner.login}/${repo.name}`)
      loadPullRequestsQuery({...searchQueries, first: 25})
    }
    return disposePullRequests
  }, [disposePullRequests, loadPullRequestsQuery, repo])

  return pullRequestsQueryRef
}

export const useBranchesQueryRef = (repo?: RepositoryPickerRepository$data) => {
  const [branchesQueryRef, loadBranchesQuery, disposeBranches] =
    useQueryLoader<BranchPickerSearchBranchesQuery>(BranchPickerSearchBranches)

  useEffect(() => {
    if (repo) {
      loadBranchesQuery({owner: repo.owner.login, name: repo.name, first: 25})
    }
    return disposeBranches
  }, [disposeBranches, loadBranchesQuery, repo])

  return branchesQueryRef
}

export type DevelopmentSectionBaseProps = {
  onIssueUpdate?: () => void
  prefetchInteractionDataEnabled?: boolean
  singleKeyShortcutsEnabled: boolean
}

type DevelopmentSectionInternalProps = DevelopmentSectionBaseProps & {
  issue: DevelopmentSectionFragment$key
}

export function DevelopmentSectionFallback() {
  return (
    <Section
      sectionHeader={<ReadonlySectionHeader title={LABELS.sectionTitles.development} />}
      emptyText={LABELS.emptySections.development}
    />
  )
}

export function DevelopmentSection({
  issue,
  singleKeyShortcutsEnabled,
  prefetchInteractionDataEnabled,
}: DevelopmentSectionInternalProps) {
  const [isCreateBranchDialogOpen, setCreateBranchDialogOpen] = useState(false)

  // TODO: use the `onIssueUpdate` callback once editing is implemented
  const data = useFragment(developmentSectionFragment, issue)
  const {
    id: issueId,
    title,
    number,
    repository: {
      owner: {login: owner},
      name: repo,
    },
  } = data
  const readonly = !data.viewerCanUpdateNext

  const [linkedBranches, linkedPullRequests] = useMemo(
    () => [getLinkedBranches(data), getLinkedPullRequests(data)],
    [data],
  )

  const hasSomeData = linkedBranches.length > 0 || linkedPullRequests.length > 0

  const [currentRepo, setCurrentRepo] = useState<{owner: string; name: string}>({owner, name: repo})
  const [branchNextStep, setBranchNextStep] = useState<BranchNextStep>('none')
  const [newBranchName, setNewBranchName] = useState<string | null>(null)

  const onHotKeyPress = useCallback(() => {
    setCreateBranchDialogOpen(true)
  }, [])

  useKeyPress(singleKeyShortcutsEnabled ? [HOTKEYS.developmentSection] : [], onHotKeyPress, {
    triggerWhenInputElementHasFocus: false,
    triggerWhenPortalIsActive: false,
  })

  const noBranchesOrLinkedPullRequests = useCallback(
    (linkText: string, before?: string, after?: string) => {
      return (
        <NoBranchesOrLinkedPullRequests
          issueId={issueId}
          title={title}
          number={number}
          owner={currentRepo.owner}
          repo={currentRepo.name}
          before={before}
          after={after}
          linkText={linkText}
          reportCreateBranchDialogOpen={setCreateBranchDialogOpen}
          setBranchNextStep={setBranchNextStep}
          setNewBranchName={setNewBranchName}
        />
      )
    },
    [currentRepo.name, currentRepo.owner, issueId, number, title],
  )

  const developmentSectionHeader = useMemo(() => {
    if (readonly) {
      return <ReadonlySectionHeader title={LABELS.sectionTitles.development} />
    }
    return prefetchInteractionDataEnabled ? (
      <Suspense fallback={<ReadonlySectionHeader title={LABELS.sectionTitles.development} />}>
        <DevelopmentSectionEditorNew
          repo={repo}
          owner={owner}
          issueId={issueId}
          anchorElement={props => <SectionHeader title={LABELS.sectionTitles.development} buttonProps={props} />}
          repoPickerSubtitle={noBranchesOrLinkedPullRequests(
            'create a branch',
            'Select a repository to search for branches and pull reqests or',
          )}
          prAndBranchPickerSubtitle={noBranchesOrLinkedPullRequests(
            'create a branch',
            'Link a branch, pull request, or',
          )}
          isCreateBranchDialogOpen={isCreateBranchDialogOpen}
          linkedBranches={linkedBranches}
          linkedPullRequests={linkedPullRequests}
          setCurrentRepo={setCurrentRepo}
        />
      </Suspense>
    ) : (
      <LazyDevelopmentSectionEditorOnClick
        repo={repo}
        owner={owner}
        issueId={issueId}
        repoPickerSubtitle={noBranchesOrLinkedPullRequests(
          'create a branch',
          'Select a repository to search for branches and pull reqests or',
        )}
        prAndBranchPickerSubtitle={noBranchesOrLinkedPullRequests('create a branch', 'Link a branch, pull request, or')}
        isCreateBranchDialogOpen={isCreateBranchDialogOpen}
        linkedBranches={linkedBranches}
        linkedPullRequests={linkedPullRequests}
        setCurrentRepo={setCurrentRepo}
        shortcutEnabled={singleKeyShortcutsEnabled}
      />
    )
  }, [
    readonly,
    prefetchInteractionDataEnabled,
    repo,
    owner,
    issueId,
    noBranchesOrLinkedPullRequests,
    isCreateBranchDialogOpen,
    linkedBranches,
    linkedPullRequests,
    singleKeyShortcutsEnabled,
  ])

  const copilotWorkspaceEnabled = useFeatureFlag('copilot_workspace')

  return (
    <Section sectionHeader={developmentSectionHeader}>
      {copilotWorkspaceEnabled && (
        // When the `copilot_workspace` feature flag is enabled,
        // display an "Open in workspace" button that allows users
        // to open the current issue in Copilot Workspace.
        // For more information about Copilot Workspace,
        // see https://githubnext.com/projects/copilot-workspace
        <Box sx={{px: 2, py: 2}}>
          <Button
            leadingVisual={CopilotIcon}
            as="a"
            href={`https://copilot-workspace.githubnext.com/gh/${owner}/${repo}/issues/${number}`}
            sx={{width: '100%'}}
          >
            Open in workspace
          </Button>
        </Box>
      )}
      {!hasSomeData && (
        <Text sx={{fontSize: 0, px: 2, mb: 2, mt: 1, color: 'fg.muted', display: 'block'}}>
          {!readonly &&
            noBranchesOrLinkedPullRequests('Create a branch', undefined, 'for this issue or link a pull request.')}
          {readonly && LABELS.emptySections.development}
        </Text>
      )}
      {branchNextStep === 'local' && (
        <BranchNextStepLocal branch={newBranchName} onClose={() => setBranchNextStep('none')} />
      )}
      {branchNextStep === 'desktop' && (
        <BranchNextStepDesktop
          owner={owner}
          repository={repo}
          branch={newBranchName}
          onClose={() => setBranchNextStep('none')}
        />
      )}
      <ActionList variant={'full'} data-testid={TEST_IDS.linkedPullRequestContainer}>
        <LinkedBranches linkedBranches={linkedBranches} />
        <LinkedPullRequests linkedPullRequests={linkedPullRequests} />
      </ActionList>
    </Section>
  )
}

export type LinkedBranch = {
  name: string
  repositoryWithOwner: string
  repositoryId: string
  id: string
}

function getLinkedPullRequests(developmentData: DevelopmentSectionFragment$data): PullRequestPickerPullRequest$data[] {
  return (
    developmentData.closedByPullRequestsReferences?.edges
      ?.flatMap(edge => {
        if (edge === null || edge.node === null) return []
        // eslint-disable-next-line no-restricted-syntax
        return readInlineData<PullRequestPickerPullRequest$key>(PullRequestPickerFragment, edge.node)
      })
      .slice(0, VALUES.maxLinkedPullRequests) || []
  )
}

function getLinkedBranches(developmentData: DevelopmentSectionFragment$data): BranchPickerRef$data[] {
  return (
    developmentData.linkedBranches?.edges
      ?.flatMap(edge => {
        if (edge === null || edge.node === null || edge.node.ref === null) return []
        // eslint-disable-next-line no-restricted-syntax
        return readInlineData<BranchPickerRef$key>(BranchPickerRefFragment, edge.node.ref)
      })
      .slice(0, VALUES.maxLinkedBranches) || []
  )
}

type LazyDevelopmentSectionEditorOnClickProps = {
  repo: string
  owner: string
  issueId: string
  linkedPullRequests: PullRequestPickerPullRequest$data[]
  isCreateBranchDialogOpen: boolean
  linkedBranches: BranchPickerRef$data[]
  setCurrentRepo: (repo: {owner: string; name: string}) => void
  repoPickerSubtitle: React.ReactElement
  prAndBranchPickerSubtitle: React.ReactElement
  shortcutEnabled: boolean
}

function LazyDevelopmentSectionEditorOnClick({
  repo,
  owner,
  issueId,
  linkedPullRequests,
  isCreateBranchDialogOpen,
  linkedBranches,
  setCurrentRepo,
  prAndBranchPickerSubtitle,
  repoPickerSubtitle,
  shortcutEnabled,
}: LazyDevelopmentSectionEditorOnClickProps) {
  const [wasTriggered, setWasTriggered] = useState(false)

  useKeyPress(shortcutEnabled ? [HOTKEYS.developmentSection] : [], (e: KeyboardEvent) => {
    if (!wasTriggered) {
      setWasTriggered(true)
      e.preventDefault()
    }
  })

  if (!wasTriggered) {
    return (
      <SectionHeader title={LABELS.sectionTitles.development} buttonProps={{onClick: () => setWasTriggered(true)}} />
    )
  }
  return (
    <Suspense
      fallback={
        <SelectPanel
          loading={true}
          placeholderText={'Search pull requests'}
          open
          onOpenChange={noop}
          onSelectedChange={noop}
          items={[]}
          onFilterChange={noop}
          selected={undefined}
          renderAnchor={props => <SectionHeader title={LABELS.sectionTitles.development} buttonProps={props} />}
          overlayProps={{width: 'medium', height: 'auto'}}
        />
      }
    >
      <DevelopmentSectionEditorNew
        repo={repo}
        owner={owner}
        issueId={issueId}
        anchorElement={props => <SectionHeader title={LABELS.sectionTitles.development} buttonProps={props} />}
        repoPickerSubtitle={repoPickerSubtitle}
        prAndBranchPickerSubtitle={prAndBranchPickerSubtitle}
        isCreateBranchDialogOpen={isCreateBranchDialogOpen}
        linkedBranches={linkedBranches}
        linkedPullRequests={linkedPullRequests}
        setCurrentRepo={setCurrentRepo}
        triggerOpen={true}
      />
    </Suspense>
  )
}

try{ DevelopmentSectionFallback.displayName ||= 'DevelopmentSectionFallback' } catch {}
try{ DevelopmentSection.displayName ||= 'DevelopmentSection' } catch {}
try{ LazyDevelopmentSectionEditorOnClick.displayName ||= 'LazyDevelopmentSectionEditorOnClick' } catch {}